/**
 * Studio "Private decks" section.
 *
 * Surfaces the private (Access-gated) decks inside the Studio so an author
 * doesn't have to remember the `/private` URL. Data comes from
 * `usePrivateDeckList()` — the dev registry locally, and the
 * `/private/private-decks.json` manifest when deployed (see that hook).
 *
 * Each card links to the private viewer and carries a Share control (issue
 * #8): define a per-deck share subdomain, or manage an existing one. The
 * share CRUD goes through `useShares()` → `/api/admin/shares`.
 *
 * Each card's link is a real `<a href>` (NOT a React Router `<Link>`) because
 * the private deck viewer lives in a SEPARATE bundle:
 *   - Deployed: `/private/decks/<slug>` — a cross-bundle, full-page load into
 *     the Access-gated private app.
 *   - Dev: `/admin/decks/<slug>` — the in-bundle admin viewer (the private
 *     bundle isn't served under `vite dev`, but the dev registry can resolve
 *     the deck, so the admin viewer renders it).
 *
 * Renders nothing when there are no private decks (e.g. a deployed instance
 * with none, or before the manifest fetch resolves), so the Studio stays
 * uncluttered for public-only setups.
 */
import { useState } from "react";
import { usePrivateDeckList } from "@/lib/decks-registry";
import { useShares } from "@/lib/use-shares";
import { ShareDialog } from "./ShareDialog";

/**
 * Where a private-deck card points. In the production bundle the private
 * viewer only exists under `/private`; in dev we fall back to the in-bundle
 * admin viewer. `import.meta.env.PROD` folds to a literal at build time.
 */
const PRIVATE_VIEW_BASE = import.meta.env.PROD
  ? "/private/decks/"
  : "/admin/decks/";

export function PrivateDecksSection() {
  const { decks } = usePrivateDeckList();
  const { shares, createShare, updateShare, deleteShare } = useShares();
  const [dialogSlug, setDialogSlug] = useState<string | null>(null);

  // Nothing to show (no private decks, or the manifest hasn't resolved /
  // failed closed). Keep the Studio clean for public-only instances.
  if (decks.length === 0) return null;

  const shareBySlug = new Map(shares.map((s) => [s.slug, s]));
  const dialogDeck = dialogSlug
    ? (decks.find((d) => d.slug === dialogSlug) ?? null)
    : null;
  const dialogShare = dialogSlug ? (shareBySlug.get(dialogSlug) ?? null) : null;

  return (
    <section
      data-testid="admin-private-section"
      className="flex flex-col gap-4 border-t border-cf-border pt-8"
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-medium tracking-[-0.025em] text-cf-text">
            Private decks
          </h2>
          <span className="rounded-full border border-cf-orange/40 bg-cf-orange-light px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.16em] text-cf-orange">
            Access-gated
          </span>
        </div>
        <p className="text-sm text-cf-text-muted">
          Local-only decks served behind Cloudflare Access at{" "}
          <code className="rounded bg-cf-bg-200 px-1 py-0.5 font-mono text-[12px] text-cf-text">
            /private
          </code>
          . Visible here because you are signed in.
        </p>
      </div>

      <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {decks.map((meta) => {
          const existing = shareBySlug.get(meta.slug);
          return (
            <li key={meta.slug}>
              <div className="group flex h-full flex-col gap-2 rounded-xl border border-cf-border bg-cf-bg-100 p-5 transition-colors hover:border-dashed hover:border-cf-orange">
                <a
                  href={`${PRIVATE_VIEW_BASE}${meta.slug}`}
                  data-testid={`private-deck-${meta.slug}`}
                  className="flex flex-col gap-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-cf-text-subtle">
                      {meta.date}
                    </span>
                    <span className="rounded-full border border-cf-orange/40 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.16em] text-cf-orange">
                      Private
                    </span>
                  </div>
                  <h3 className="text-lg font-medium tracking-[-0.02em] text-cf-text">
                    {meta.title}
                  </h3>
                  {meta.description ? (
                    <p className="text-sm leading-relaxed text-cf-text-muted">
                      {meta.description}
                    </p>
                  ) : null}
                </a>

                <div className="mt-auto flex items-center justify-between gap-2 border-t border-cf-border pt-3">
                  {existing ? (
                    <span
                      data-testid={`share-status-${meta.slug}`}
                      title={existing.host}
                      className="truncate font-mono text-[10px] uppercase tracking-[0.16em] text-cf-orange"
                    >
                      Shared
                    </span>
                  ) : (
                    <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-cf-text-subtle">
                      Not shared
                    </span>
                  )}
                  <button
                    type="button"
                    data-interactive
                    data-testid={`share-btn-${meta.slug}`}
                    onClick={() => setDialogSlug(meta.slug)}
                    className="cf-btn-ghost text-[10px]"
                  >
                    {existing ? "Manage" : "Share"}
                  </button>
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      <ShareDialog
        isOpen={dialogSlug !== null}
        deckSlug={dialogDeck?.slug ?? ""}
        deckTitle={dialogDeck?.title}
        existingShare={dialogShare}
        onClose={() => setDialogSlug(null)}
        onCreate={async (input) => {
          await createShare(input);
        }}
        onUpdate={async (host, allowlist) => {
          await updateShare(host, allowlist);
        }}
        onDelete={async (host) => {
          await deleteShare(host);
        }}
      />
    </section>
  );
}
